import {
  enumSalesLoadLineItemPhase,
  enumUnit,
  SalesItemLineType,
  Unit,
} from '@rematter/frontend-lib-api-client';

import {
  enumLoadWeightType,
  LoadFormLineItem,
  LoadFormState,
  LoadLineItemRecord,
  LoadRecord,
} from '../types/load';

import { toMoneyInput, zeroMoneyInput } from './money';
import { toWeightInput } from './weight';

type PartialLoadLineItem = Omit<Partial<LoadLineItemRecord>, 'id'> & {
  id: string;
};

export const getLoadLineItems = (lineItems: PartialLoadLineItem[]) => {
  const shippedLineItems = lineItems.filter(
    (item) => item?.phase === enumSalesLoadLineItemPhase.Shipped,
  );
  const requestedLineItems = lineItems.filter(
    (item) =>
      item?.phase === enumSalesLoadLineItemPhase.Requested || !item?.phase,
  );
  const relevantLineItems = shippedLineItems.length
    ? shippedLineItems
    : requestedLineItems;

  const materialItemIds = [
    ...new Set(
      relevantLineItems
        .map((li) => li?.itemId)
        .filter((id): id is string => !!id),
    ),
  ];

  return {
    relevantLineItems,
    materialItemIds,
    shippedLineItems,
    requestedLineItems,
  };
};

type LineItems = LoadRecord['lineItems'] | LoadFormState['lineItems'];

/**
 * Creates a base form line item from a load line item record
 */
const createBaseFormLineItem = (
  lineItem: LoadLineItemRecord,
): Omit<LoadFormLineItem, 'aggregatedTags'> => ({
  id: lineItem.id,
  type: lineItem.type,
  itemId: lineItem.itemId,
  item: lineItem.item,
  tagId: lineItem.tagId,
  unitPrice: lineItem.unitPrice,
  estimatedPrice: lineItem.estimatedPrice,
  pricingUnit: lineItem.pricingUnit,
  phase: lineItem.phase,
  weights: lineItem.weights,
  includeInSalesOrder: false,
});

/**
 * Creates aggregated tags array for a line item
 * Returns array with tag if tagId exists, otherwise empty array
 */
const createAggregatedTags = (
  lineItem: LoadLineItemRecord,
  netWeight: number,
) =>
  lineItem.tagId != null
    ? [
        {
          id: lineItem.tagId,
          netQuantity: netWeight,
          lineItemId: lineItem.id,
          weights: lineItem.weights,
        },
      ]
    : [];

/**
 * Sums weight values across weight types
 */
const sumWeights = (
  existingWeights: LoadFormLineItem['weights'],
  newWeights: LoadLineItemRecord['weights'],
) => {
  if (!existingWeights || !newWeights) {
    return newWeights ?? existingWeights;
  }

  const weightTypes = [
    enumLoadWeightType.Gross,
    enumLoadWeightType.Tare,
    enumLoadWeightType.Net,
  ] as const;

  return weightTypes.reduce(
    (acc, weightType) => {
      const existingValue = Number(existingWeights[weightType]?.value ?? 0);
      const newValue = Number(newWeights[weightType]?.value ?? 0);
      const unit =
        existingWeights[weightType]?.unit ??
        newWeights[weightType]?.unit ??
        enumUnit.Pounds;

      acc[weightType] = {
        value: existingValue + newValue,
        unit,
      };
      return acc;
    },
    {} as NonNullable<LoadFormLineItem['weights']>,
  );
};

/**
 * Maps load line items from API response to form state.
 * Aggregates Tagged items by composite key (itemId + phase),
 * while keeping Raw items separate.
 *
 * For Tagged items with empty tagId (untagged weight), the weight is tracked
 * separately and included in the total weight calculation.
 */
export const mapLoadLineItemsToFormLineItems = (
  lineItems: LineItems,
): LoadFormLineItem[] => {
  // Track untagged weight and lineItemId per composite key
  const untaggedWeightMap = new Map<
    string,
    { weight: number; lineItemId?: string }
  >();

  const aggregatedMap = lineItems.reduce<Map<string, LoadFormLineItem>>(
    (acc, item) => {
      const lineItem = item as LoadLineItemRecord;

      // Don't aggregate Raw items - add them directly with unique key
      if (lineItem.type === 'Raw') {
        acc.set(`raw_${lineItem.id}`, {
          ...createBaseFormLineItem(lineItem),
          aggregatedTags: createAggregatedTags(
            lineItem,
            lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0,
          ),
        });
        return acc;
      }

      // For Tagged items, aggregate by composite key
      const compositeKey = `${lineItem.itemId}_${lineItem.phase}`;
      const existingItem = acc.get(compositeKey);
      const netWeight = lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0;

      // Track untagged weight separately
      if (!lineItem.tagId || lineItem.tagId === '') {
        const existing = untaggedWeightMap.get(compositeKey) ?? { weight: 0 };
        untaggedWeightMap.set(compositeKey, {
          weight: Number(existing.weight) + Number(netWeight),
          lineItemId: existing.lineItemId ?? lineItem.id,
        });
      }

      if (existingItem) {
        // Aggregate tags and sum weights
        acc.set(compositeKey, {
          ...existingItem,
          aggregatedTags: [
            ...(existingItem.aggregatedTags ?? []),
            ...createAggregatedTags(lineItem, netWeight),
          ],
          weights: sumWeights(existingItem.weights, lineItem.weights),
        });
      } else {
        // Create new aggregated item
        acc.set(compositeKey, {
          ...createBaseFormLineItem(lineItem),
          aggregatedTags: createAggregatedTags(lineItem, netWeight),
        });
      }

      return acc;
    },
    new Map(),
  );

  // Add untagged weight information to the aggregated items
  const result = Array.from(aggregatedMap.values()).map((item) => {
    const compositeKey = `${item.itemId}_${item.phase}`;
    const untaggedData = untaggedWeightMap.get(compositeKey);

    if (untaggedData && untaggedData.weight > 0 && untaggedData.lineItemId) {
      return {
        ...item,
        untaggedLineItem: {
          id: untaggedData.lineItemId,
          netQuantity: untaggedData.weight,
        },
      };
    }

    return item;
  });

  return result;
};

/**
 * Calculates estimated price based on unit price and weight
 */
const calculateEstimatedPrice = (
  unitPrice: LoadFormLineItem['unitPrice'],
  weight: number,
) => {
  if (!unitPrice) return null;

  return toMoneyInput({
    amount: unitPrice.amount * weight,
    currencyCode: unitPrice.currencyCode,
  });
};

/**
 * Creates weight inputs for all weight types.
 * If weights exist with at least Net weight, use those actual values.
 * Otherwise fall back to creating weights from provided netWeight/unit (for aggregated tags).
 */
const createWeightInputs = (
  weights: LoadFormLineItem['weights'],
  netWeight?: number,
  unit?: Unit,
) => {
  // If actual weights exist with at least a Net value, use them
  if (weights?.[enumLoadWeightType.Net]?.value !== undefined) {
    return {
      [enumLoadWeightType.Net]: toWeightInput(weights[enumLoadWeightType.Net]),
      [enumLoadWeightType.Gross]: toWeightInput(
        weights[enumLoadWeightType.Gross],
      ),
      [enumLoadWeightType.Tare]: toWeightInput(
        weights[enumLoadWeightType.Tare],
      ),
    };
  }

  // For aggregated tags without existing weights - use provided net weight
  if (netWeight !== undefined && unit !== undefined) {
    return {
      [enumLoadWeightType.Net]: toWeightInput({ value: netWeight, unit }),
      [enumLoadWeightType.Gross]: toWeightInput({ value: netWeight, unit }),
      [enumLoadWeightType.Tare]: toWeightInput({ value: 0, unit }),
    };
  }

  // Fallback: return null weights
  return {
    [enumLoadWeightType.Net]: null,
    [enumLoadWeightType.Gross]: null,
    [enumLoadWeightType.Tare]: null,
  };
};

/**
 * Creates a mapping of tagId to lineItemId from the original load data.
 * Used to preserve original line item IDs when transforming form data back to API input.
 */
export const createTagToLineItemMap = (
  lineItems: LoadLineItemRecord[],
): Map<string, string> => {
  const tagToLineItemMap = new Map<string, string>();

  for (const lineItem of lineItems) {
    if (lineItem.tagId) {
      tagToLineItemMap.set(lineItem.tagId, lineItem.id);
    }
  }

  return tagToLineItemMap;
};

/**
 * Helper to create a line item input object with shared properties
 */
const withWeightUnit = (
  weight: ReturnType<typeof toWeightInput>,
  unit: Unit,
) => (weight ? { ...weight, unit } : weight);

const buildWeights = (
  item: LoadFormLineItem,
  options: {
    weights?: LoadLineItemRecord['weights'];
    netWeight: number;
    weightUnit: Unit;
  },
) => {
  const isEachPricing = item.pricingUnit === enumUnit.Each;
  const effectiveUnit = isEachPricing ? enumUnit.Each : options.weightUnit;

  const baseWeights = options.weights
    ? {
        [enumLoadWeightType.Net]: toWeightInput(
          options.weights[enumLoadWeightType.Net],
        ),
        [enumLoadWeightType.Gross]: toWeightInput(
          options.weights[enumLoadWeightType.Gross],
        ),
        [enumLoadWeightType.Tare]: toWeightInput(
          options.weights[enumLoadWeightType.Tare],
        ),
      }
    : createWeightInputs(item.weights, options.netWeight, effectiveUnit);

  if (!isEachPricing) return baseWeights;

  return {
    [enumLoadWeightType.Net]: withWeightUnit(
      baseWeights[enumLoadWeightType.Net],
      enumUnit.Each,
    ),
    [enumLoadWeightType.Gross]: withWeightUnit(
      baseWeights[enumLoadWeightType.Gross],
      enumUnit.Each,
    ),
    [enumLoadWeightType.Tare]: withWeightUnit(
      baseWeights[enumLoadWeightType.Tare],
      enumUnit.Each,
    ),
  };
};

const createLineItemInput = (
  item: LoadFormLineItem,
  options: {
    id?: string;
    tagId?: string | null;
    netWeight: number;
    weights?: LoadLineItemRecord['weights'];
    weightUnit: Unit;
  },
) => ({
  id: options.id,
  type: item.type as SalesItemLineType,
  itemId: item.itemId,
  tagId: options.tagId ?? null,
  weights: buildWeights(item, options),
  phase: item.phase,
  unitPrice: item.unitPrice ? toMoneyInput(item.unitPrice) : zeroMoneyInput(),
  estimatedPrice: calculateEstimatedPrice(item.unitPrice, options.netWeight),
  pricingUnit: item.pricingUnit ?? enumUnit.Pounds,
});

/**
 * Transforms form line items to API input format.
 * Splits Tagged items with aggregatedTags into individual line items per tag,
 * and includes untagged line items if present.
 * Keeps Raw items and Tagged items without aggregatedTags as-is.
 */
export const transformFormLineItemsToInput = (
  lineItems: LoadFormLineItem[],
  tagToLineItemMap?: Map<string, string>,
) =>
  lineItems.flatMap((item) => {
    const hasAggregatedTags =
      item.type === 'Tagged' &&
      item.aggregatedTags &&
      item.aggregatedTags.length > 0;

    // Split Tagged items with aggregatedTags into individual line items per tag
    if (hasAggregatedTags && item.aggregatedTags) {
      const weightUnit =
        item.weights?.[enumLoadWeightType.Net]?.unit ?? enumUnit.Pounds;
      const totalNetWeight = Number(
        item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
      );

      // Calculate remaining weight (total - sum of tagged weights)
      const sumOfTaggedWeights = item.aggregatedTags.reduce(
        (sum, tag) => sum + Number(tag.netQuantity),
        0,
      );
      const remainingWeight = totalNetWeight - sumOfTaggedWeights;

      // Use reduce to build array with tagged items and optional untagged item
      return item.aggregatedTags.reduce<
        ReturnType<typeof createLineItemInput>[]
      >(
        (acc, tag) => {
          const lineItemId =
            tag.lineItemId ??
            (tag.id ? tagToLineItemMap?.get(tag.id) : undefined);

          return [
            ...acc,
            createLineItemInput(item, {
              id: lineItemId,
              tagId: tag.id ?? null,
              netWeight: tag.netQuantity,
              weights: tag.weights,
              weightUnit,
            }),
          ];
        },
        remainingWeight > 0
          ? [
              // Prepend untagged item if there's remaining weight
              createLineItemInput(item, {
                id: item.untaggedLineItem?.id, // undefined for new, or existing id
                tagId: null,
                netWeight: remainingWeight,
                weights: undefined,
                weightUnit,
              }),
            ]
          : [],
      );
    }

    // For Raw items and Tagged items without aggregatedTags
    const netWeight = item.weights?.[enumLoadWeightType.Net]?.value || 0;

    return [
      createLineItemInput(item, {
        id:
          item.id.startsWith('temp-') || item.id.startsWith('so-')
            ? undefined
            : item.id,
        tagId: item.type === 'Tagged' ? (item.tagId ?? null) : item.tagId,
        netWeight,
        weights: undefined,
        weightUnit:
          item.weights?.[enumLoadWeightType.Net]?.unit ?? enumUnit.Pounds,
      }),
    ];
  });
